<template>
    <div class="flex items-center justify-between mb-8">
        <div class="flex items-center space-x-4">
            <Head :title="$t('admin.globalBranding')" />
            <Link :href="route('advisors.templates.index')" class="p-2 bg-white rounded-full shadow-sm border border-gray-100 text-gray-400 hover:text-indigo-600 hover:border-indigo-200 transition-all active:scale-95 group">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
            </Link>
            <page-title :title="$t('admin.globalBranding')"></page-title>
        </div>
    </div>

    <div class="mt-10 sm:mt-0">
        <div class="flex flex-col lg:flex-row gap-8">
            <!-- Left Side: Preview -->
            <div class="lg:w-1/3">
                <div class="sticky top-6">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">
                        {{ $t('preview') || 'Vorschau' }}
                    </h3>
                    <branding-preview 
                        :navbar-color="form.navbar_color"
                        :sidebar-color="form.sidebar_color"
                        :navbar-text-color="form.navbar_text_color"
                        :sidebar-text-color="form.sidebar_text_color"
                        :logo-url="logoPreviewUrl || settings.logo_url"
                    />
                </div>
            </div>

            <!-- Right Side: Form -->
            <div class="lg:w-2/3">
                <div class="px-4 sm:px-0 mb-6 lg:hidden">
                    <h3 class="text-lg font-medium text-gray-900">
                        Globales Branding
                    </h3>
                    <p class="mt-1 text-sm text-gray-600">
                        Diese Einstellungen gelten für alle Agenturen, die kein eigenes Branding festgelegt haben.
                    </p>
                </div>
                
                <form @submit.prevent="submit">
                    <div class="shadow-lg sm:rounded-xl overflow-hidden border border-gray-100 bg-white">
                        <div class="px-6 py-8">
                            <div class="space-y-8">
                                
                                <!-- Logo Section -->
                                <div class="border-b border-gray-100 pb-8 last:border-0 last:pb-0">
                                    <label class="block text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4">Logo</label>
                                    <div class="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-8">
                                        <div class="flex-shrink-0">
                                            <div v-if="logoPreviewUrl || settings.logo_url" class="p-2 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 group hover:border-indigo-300 transition-colors w-[240px] h-[120px] flex items-center justify-center overflow-hidden">
                                                <img :src="logoPreviewUrl || settings.logo_url" class="max-w-full max-h-full object-contain" alt="Global Logo" />
                                            </div>
                                            <div v-else class="w-[240px] h-[120px] bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400">
                                                <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                            </div>
                                        </div>
                                        <div class="flex-1 space-y-3 w-full">
                                            <div class="flex items-center justify-center w-full">
                                                <label class="flex flex-col items-center justify-center w-full h-[80px] border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                                    <div class="flex flex-col items-center justify-center pt-2 pb-2 text-center">
                                                        <svg class="w-6 h-6 mb-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                                                        <p class="text-[11px] text-gray-500 font-medium px-2"><span class="font-bold text-indigo-600">{{ $t('click_to_upload') || 'Klicken zum Hochladen' }}</span> oder Drag & Drop</p>
                                                    </div>
                                                    <input type="file" class="hidden" @change="handleFileUpload" :accept="allowedMimesAccept" />
                                                </label>
                                            </div>
                                            <p class="text-[11px] text-red-400 italic">Erlaubte Formate: {{ settings.logo_upload_mimes.toUpperCase() }}, Max {{ maxSizeInMb }} MB. Empfohlene Maße: 240x120 Pixel.</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Colors Section -->
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                                    <!-- Navbar Color -->
                                    <div class="space-y-3">
                                        <label for="navbar_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">Navbar Farbe</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="navbar_color"
                                                    id="navbar_color"
                                                    v-model="form.navbar_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.navbar_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Sidebar Color -->
                                    <div class="space-y-3">
                                        <label for="sidebar_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">Sidebar Farbe</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="sidebar_color"
                                                    id="sidebar_color"
                                                    v-model="form.sidebar_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.sidebar_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Navbar Text Color -->
                                    <div class="space-y-3">
                                        <label for="navbar_text_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">Navbar Text Farbe</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="navbar_text_color"
                                                    id="navbar_text_color"
                                                    v-model="form.navbar_text_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.navbar_text_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Sidebar Text Color -->
                                    <div class="space-y-3">
                                        <label for="sidebar_text_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">Sidebar Text Farbe</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="sidebar_text_color"
                                                    id="sidebar_text_color"
                                                    v-model="form.sidebar_text_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.sidebar_text_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Email Background Color -->
                                    <div class="space-y-3">
                                        <label for="email_background_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">Email Hintergrundfarbe</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="email_background_color"
                                                    id="email_background_color"
                                                    v-model="form.email_background_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.email_background_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div class="px-6 py-5 bg-gray-50/50 border-t border-gray-100 flex justify-end items-center">
                            <span v-if="form.isDirty" class="text-xs text-amber-600 mr-4 flex items-center animate-pulse">
                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                                Nicht gespeicherte Änderungen
                            </span>
                            <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="h-11 px-8 rounded-xl shadow-md transform transition-all active:scale-95">
                                <span v-if="form.processing" class="flex items-center">
                                    <svg class="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    Speichert...
                                </span>
                                <span v-else>{{ $t("publish") || 'Veröffentlichen' }}</span>
                            </primary-button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import PageTitle from "@/Shared/Components/PageTitle";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import BrandingPreview from "@/Shared/Components/BrandingPreview";
import { useForm, usePage, Link, Head } from "@inertiajs/inertia-vue3";
import { ref, onUnmounted } from "vue";
import { notify } from "notiwind";
import route from "ziggy";

export default {
    components: {
        Head,
        PageTitle,
        PrimaryButton,
        SecondaryButton,
        Link,
        BrandingPreview,
    },
    props: ["settings"],
    layout: [MainLayout],
    setup(props) {
        const { props: pageProps } = usePage();
        const logoPreviewUrl = ref(null);

        onUnmounted(() => {
            if (logoPreviewUrl.value) {
                URL.revokeObjectURL(logoPreviewUrl.value);
            }
        });

        const allowedMimesList = (props.settings.logo_upload_mimes || 'png,svg').split(',').map(m => m.trim().toLowerCase());
        const allowedMimesAccept = allowedMimesList.map(m => m === 'svg' ? '.svg,image/svg+xml' : `image/${m}`).join(',');
        
        const form = useForm({
            logo: null,
            navbar_color: props.settings.navbar_color || "#2f3441",
            sidebar_color: props.settings.sidebar_color || "#2f3441",
            navbar_text_color: props.settings.navbar_text_color || "#ffffff",
            sidebar_text_color: props.settings.sidebar_text_color || "#ffffff",
            email_background_color: props.settings.email_background_color || "#ffffff",
            remove_logo: false,
            password: null,
        });

        const ensureSwal = async () => {
            if (window.Swal) return window.Swal;
            await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/sweetalert2@11';
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
            return window.Swal;
        };

        const submit = () => {
            ensureSwal().then((Swal) => {
                Swal.fire({
                    title: 'Globales Branding speichern',
                    text: 'Bitte geben Sie das Passwort ein, um die globalen Branding-Einstellungen zu aktualisieren:',
                    input: 'password',
                    inputAttributes: {
                        autocapitalize: 'off',
                        autocomplete: 'new-password'
                    },
                    showCancelButton: true,
                    confirmButtonText: 'Speichern',
                    cancelButtonText: 'Abbrechen',
                    showLoaderOnConfirm: true,
                    preConfirm: (password) => {
                        if (!password) {
                            Swal.showValidationMessage('Passwort ist erforderlich');
                            return false;
                        }
                        return password;
                    },
                    allowOutsideClick: () => !Swal.isLoading()
                }).then((result) => {
                    if (result.isConfirmed) {
                        form.password = result.value;
                        form.transform((data) => ({
                            ...data,
                            _method: "PUT",
                        })).post(route("admin.branding.update", undefined, undefined, pageProps.value.ziggy), {
                            forceFormData: true,
                            preserveScroll: true,
                            onSuccess: () => {
                                notify({
                                    group: "success",
                                    title: "Erfolg",
                                    text: "Branding-Einstellungen wurden erfolgreich aktualisiert."
                                }, 3000);
                            },
                            onError: (errors) => {
                                if (errors.password) {
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Fehler',
                                        text: errors.password
                                    });
                                } else {
                                    notify({
                                        group: "error",
                                        title: "Fehler",
                                        text: "Beim Speichern der Einstellungen ist ein Fehler aufgetreten."
                                    }, 3000);
                                }
                            }
                        });
                    }
                });
            });
        };

        const maxBytes = props.settings.max_logo_upload_size || (3 * 1024 * 1024);
        const maxSizeInMb = Math.round(maxBytes / (1024 * 1024));

        const handleFileUpload = (event) => {
            const file = event.target.files[0];
            if (file) {
                const fileExt = file.name.split('.').pop().toLowerCase();
                const fileType = file.type;

                const isAllowed = allowedMimesList.some(mime => {
                    if (mime === 'svg') return fileType === 'image/svg+xml' || fileExt === 'svg';
                    return fileType === `image/${mime}` || fileExt === mime;
                });

                if (!isAllowed) {
                    notify({
                        group: "error",
                        title: "Fehler",
                        text: `Bitte laden Sie nur ${props.settings.logo_upload_mimes.toUpperCase()} Dateien hoch.`
                    }, 3000);
                    event.target.value = "";
                    return;
                }
                if (file.size > maxBytes) {
                    notify({
                        group: "error",
                        title: "Fehler",
                        text: `Die Datei ist zu groß. Max ${maxSizeInMb} MB erlaubt.`
                    }, 3000);
                    event.target.value = "";
                    return;
                }
                form.logo = file;
                form.remove_logo = false;
                logoPreviewUrl.value = URL.createObjectURL(file);
            }
        };

        return { form, submit, handleFileUpload, logoPreviewUrl, maxSizeInMb, allowedMimesAccept, ensureSwal };
    },
};
</script>

